import { Suspense, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/useAuth';
import { CLIENTES, useClienteActivo, type ClienteCodigo } from '../lib/cliente-activo';
import { useTituloActual } from '../lib/page-title';
import { Button } from './ui/Button';
import { Modal } from './ui/Modal';
import { FxWidget } from './FxWidget';
import { ContenedorWidget } from './ContenedorWidget';
import { CommandPalette } from './CommandPalette';
import { ErrorPagina } from './ErrorPagina';

/**
 * Armazón de la app tras el rediseño UX (fase 2, 16/09/2026, decisión D1):
 *   - conmutador GAME / TICNOVA arriba del menú; un solo menú de 9 entradas
 *     construido para el cliente activo (antes: 47 destinos, dos bloques
 *     casi idénticos);
 *   - buscador Ctrl+K (paleta de comandos) y atajos G I / G A / G P / G O / G C y ?;
 *   - breadcrumb con el nombre del registro abierto (lib/page-title.tsx).
 */

export type NavItem = { to: string; label: string; atajo?: string; /** Cabecera de bloque que se pinta antes de este ítem (Administración en 4 bloques). */ bloque?: string };
type NavSection =
  | { kind: 'flat'; item: NavItem }
  | { kind: 'group'; id: string; label: string; items: NavItem[]; rootTo?: string };

export function construirNav(cliente: ClienteCodigo, base: string): NavSection[] {
  const game = cliente === 'GAME';
  return [
    { kind: 'flat', item: { to: '/', label: 'Centro de mando', atajo: 'G I' } },
    { kind: 'flat', item: { to: base, label: `Panel ${cliente}` } },
    { kind: 'flat', item: { to: `${base}/analisis`, label: 'Análisis', atajo: 'G A' } },
    { kind: 'flat', item: { to: `${base}/productos`, label: 'Productos', atajo: 'G P' } },
    { kind: 'flat', item: { to: `${base}/pedidos`, label: 'Pedidos', atajo: 'G O' } },
    { kind: 'flat', item: { to: `${base}/cotizaciones`, label: 'Cotizaciones', atajo: 'G C' } },
    { kind: 'flat', item: { to: `${base}/calculadora`, label: 'Calculadora de precio' } },
    {
      kind: 'group', id: 'stock', label: 'Stock e importaciones',
      items: [
        // Un único Importador de ficheros con un cuadro por fichero (GAME y TICNOVA).
        { to: `${base}/importador-ficheros`, label: 'Importador de ficheros' },
        { to: `${base}/importaciones`, label: 'Ficheros importados' },
        { to: `${base}/reposiciones`, label: 'Reposiciones masivas' },
        ...(game ? [{ to: `${base}/historico-ventas`, label: 'Histórico de ventas' }, { to: `${base}/bolsa`, label: 'Bolsa / Provisionamiento' }] : []),
        ...(!game ? [{ to: `${base}/icecat`, label: 'ICECAT' }] : []),
      ],
    },
    { kind: 'flat', item: { to: `${base}/nuevos-productos`, label: 'Proyectos e ideas' } },
    {
      kind: 'group', id: 'proveedores', label: 'Proveedores', rootTo: '/proveedores',
      items: [
        { to: '/proveedores/listado', label: 'Proveedores' },
        { to: '/proveedores/productos', label: 'Productos de proveedor' },
        { to: '/proveedores/pedidos', label: 'Pedidos a proveedor' },
        { to: '/proveedores/importador', label: 'Importador de productos' },
      ],
    },
    { kind: 'flat', item: { to: '/comisiones', label: 'Comisiones' } },
    {
      kind: 'group', id: 'admin', label: 'Administración',
      items: [
        { to: `${base}/parametros`, label: `Parámetros ${cliente}`, bloque: 'Cliente y comercial' },
        { to: '/admin/clientes', label: 'Clientes' },
        { to: '/admin/condiciones-comerciales', label: 'Condiciones comerciales' },
        { to: '/admin/envios', label: 'Envíos' },
        { to: '/admin/pedido-estados', label: 'Estados de pedido' },
        ...(game ? [{ to: '/admin/game-calendario-fiscal', label: 'Calendario fiscal GAME' }] : []),
        { to: '/admin/email-templates', label: 'Plantillas de email' },
        { to: '/admin/marcas', label: 'Marcas', bloque: 'Catálogo' },
        { to: '/admin/gamas', label: 'Gamas' },
        { to: '/admin/categorias', label: 'Categorías' },
        { to: '/admin/subcategorias', label: 'Subcategorías' },
        { to: '/admin/detalles', label: 'Detalles' },
        { to: '/admin/producto-estados', label: 'Estados de producto' },
        ...(!game ? [{ to: '/admin/ticnova-familias-map', label: 'Familias TICNOVA', bloque: 'Importadores' }] : []),
        { to: '/admin/producto-codigos-alias', label: 'Alias códigos ERP', bloque: game ? 'Importadores' : undefined },
        { to: '/admin/proveedor-alias', label: 'Alias proveedores' },
        { to: '/admin/parametros', label: 'Parámetros globales', bloque: 'Sistema' },
      ],
    },
  ];
}

const EXPANDED_KEY = 'pm-approx:nav-expanded-v2';

function readExpanded(): Record<string, boolean> {
  try {
    const raw = window.localStorage.getItem(EXPANDED_KEY);
    if (raw) return JSON.parse(raw) as Record<string, boolean>;
  } catch { /* sin storage */ }
  return {};
}
function persistExpanded(state: Record<string, boolean>): void {
  try { window.localStorage.setItem(EXPANDED_KEY, JSON.stringify(state)); } catch { /* sin storage */ }
}

function Logo() {
  return (
    <div className="flex items-center gap-2">
      <div className="flex h-8 w-8 items-center justify-center rounded-md bg-slate-900 font-display text-xs font-extrabold text-white">PM</div>
      <span className="font-display font-bold text-slate-900">PM-APPROX</span>
    </div>
  );
}

function Chevron({ open }: { open: boolean }) {
  return (
    <svg viewBox="0 0 24 24" className={`h-3.5 w-3.5 shrink-0 text-slate-500 transition-transform ${open ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M9 5l7 7-7 7" /></svg>
  );
}

function SelectorCliente({ onPick }: { onPick?: () => void }) {
  const { cliente, setCliente } = useClienteActivo();
  return (
    <div role="group" aria-label="Cliente activo" className="flex rounded-md bg-slate-200 p-0.5">
      {CLIENTES.map((c) => (
        <button key={c.codigo} type="button" onClick={() => { setCliente(c.codigo); onPick?.(); }}
          className={`flex-1 cursor-pointer rounded px-2 py-1 text-xs font-bold transition ${cliente === c.codigo ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'}`}
          aria-pressed={cliente === c.codigo}>
          {c.nombre}
        </button>
      ))}
    </div>
  );
}

function Sidebar({ nav, onItemClick, onBuscar }: { nav: NavSection[]; onItemClick?: () => void; onBuscar: () => void }) {
  const location = useLocation();
  const [expanded, setExpanded] = useState<Record<string, boolean>>(() => readExpanded());

  // Auto-expandir el grupo de la ruta actual.
  useEffect(() => {
    const groupId = nav.find((s): s is Extract<NavSection, { kind: 'group' }> => {
      if (s.kind !== 'group') return false;
      const matchesRoot = s.rootTo && (location.pathname === s.rootTo || location.pathname.startsWith(`${s.rootTo}/`));
      const matchesItem = s.items.some((i) => location.pathname === i.to || location.pathname.startsWith(`${i.to}/`));
      return Boolean(matchesRoot || matchesItem);
    })?.id;
    if (groupId && !expanded[groupId]) {
      const next = { ...expanded, [groupId]: true };
      setExpanded(next); persistExpanded(next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, nav]);

  const toggle = (id: string) => { const next = { ...expanded, [id]: !expanded[id] }; setExpanded(next); persistExpanded(next); };
  const linkCls = (isActive: boolean, sub = false) =>
    `flex cursor-pointer items-center rounded-md px-3 py-1.5 transition ${isActive ? (sub ? 'bg-slate-200 font-medium text-slate-900' : 'bg-slate-900 text-white') : sub ? 'text-slate-600 hover:bg-slate-100 hover:text-slate-900' : 'text-slate-700 hover:bg-slate-200 hover:text-slate-900'}`;

  return (
    <nav className="flex h-full flex-col bg-slate-50">
      <div className="border-b border-slate-200 px-4 py-3">
        <Link to="/" className="cursor-pointer" onClick={onItemClick}><Logo /></Link>
      </div>
      <div className="space-y-2 px-3 pt-3">
        <SelectorCliente onPick={onItemClick} />
        <button type="button" onClick={onBuscar} className="flex w-full cursor-pointer items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-left text-sm text-slate-500 hover:border-slate-300 hover:text-slate-700">
          <span aria-hidden="true">⌕</span><span className="flex-1">Buscar…</span><kbd className="rounded border border-slate-200 px-1 font-mono text-[10px] text-slate-400">Ctrl K</kbd>
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-3 py-3 text-sm">
        <ul className="space-y-0.5">
          {nav.map((section) => {
            if (section.kind === 'flat') {
              return (
                <li key={section.item.to}>
                  <NavLink to={section.item.to} end={section.item.to.indexOf('/', 1) === -1} onClick={onItemClick} className={({ isActive }) => linkCls(isActive)}>
                    <span className="flex-1">{section.item.label}</span>
                    {section.item.atajo && <kbd className="font-mono text-[10px] opacity-50">{section.item.atajo}</kbd>}
                  </NavLink>
                </li>
              );
            }
            const isOpen = expanded[section.id] ?? false;
            return (
              <li key={section.id}>
                <div className="flex items-stretch gap-0.5">
                  {section.rootTo ? (
                    <NavLink to={section.rootTo} onClick={onItemClick} end className={({ isActive }) => `flex-1 cursor-pointer rounded-md px-3 py-1.5 font-medium transition ${isActive ? 'bg-slate-900 text-white' : 'text-slate-800 hover:bg-slate-200'}`}>{section.label}</NavLink>
                  ) : (
                    <button type="button" onClick={() => toggle(section.id)} className="flex-1 cursor-pointer rounded-md px-3 py-1.5 text-left font-medium text-slate-800 transition hover:bg-slate-200">{section.label}</button>
                  )}
                  <button type="button" onClick={() => toggle(section.id)} aria-label={isOpen ? 'Colapsar sección' : 'Expandir sección'} className="flex w-7 cursor-pointer items-center justify-center rounded-md hover:bg-slate-200"><Chevron open={isOpen} /></button>
                </div>
                {isOpen && (
                  <ul className="ml-3 mt-0.5 space-y-0.5 border-l border-slate-200 pl-2">
                    {section.items.map((item) => (
                      <li key={item.to}>
                        {item.bloque && <div className="mb-0.5 mt-2 px-3 text-[10px] font-bold uppercase tracking-wide text-slate-400">{item.bloque}</div>}
                        <NavLink to={item.to} onClick={onItemClick} className={({ isActive }) => linkCls(isActive, true)}>{item.label}</NavLink>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      </div>
      <div className="border-t border-slate-200 px-4 py-2 text-[11px] text-slate-400">Pulsa <kbd className="font-mono">?</kbd> para ver los atajos</div>
    </nav>
  );
}

export function Layout({ children }: { children?: ReactNode }) {
  const { user, signOut } = useAuth();
  const { cliente, base } = useClienteActivo();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [paleta, setPaleta] = useState(false);
  const [atajos, setAtajos] = useState(false);
  const nav = useMemo(() => construirNav(cliente, base), [cliente, base]);
  const menuPlano = useMemo<NavItem[]>(() => nav.flatMap((s) => (s.kind === 'flat' ? [s.item] : [...(s.rootTo ? [{ to: s.rootTo, label: s.label }] : []), ...s.items])), [nav]);

  // Atajos globales: Ctrl/Cmd+K buscador, ? atajos, G + letra navegar.
  const ultimaG = useRef(0);
  useEffect(() => {
    const enCampo = (t: EventTarget | null) => {
      const el = t as HTMLElement | null;
      if (!el) return false;
      const tag = el.tagName;
      return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || el.isContentEditable;
    };
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') { e.preventDefault(); setPaleta(true); return; }
      if (enCampo(e.target) || e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === '?') { e.preventDefault(); setAtajos((v) => !v); return; }
      const ahora = Date.now();
      if (e.key.toLowerCase() === 'g') { ultimaG.current = ahora; return; }
      if (ahora - ultimaG.current < 1200) {
        const destino: Record<string, string> = { p: `${base}/productos`, o: `${base}/pedidos`, c: `${base}/cotizaciones`, a: `${base}/analisis`, i: '/' };
        const to = destino[e.key.toLowerCase()];
        if (to) { e.preventDefault(); ultimaG.current = 0; navigate(to); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [base, navigate]);

  return (
    <div className="flex min-h-screen bg-slate-100">
      <aside className="hidden w-60 shrink-0 border-r border-slate-200 lg:block"><Sidebar nav={nav} onBuscar={() => setPaleta(true)} /></aside>

      {mobileOpen && (
        <div className="fixed inset-0 z-40 flex lg:hidden">
          <div className="flex-1 bg-slate-900/40" onClick={() => setMobileOpen(false)} />
          <aside className="w-60 border-r border-slate-200 bg-white"><Sidebar nav={nav} onItemClick={() => setMobileOpen(false)} onBuscar={() => { setMobileOpen(false); setPaleta(true); }} /></aside>
        </div>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-12 shrink-0 items-center justify-between border-b border-slate-200 bg-white px-4">
          <button className="cursor-pointer rounded-md p-1.5 text-slate-600 hover:bg-slate-100 lg:hidden" onClick={() => setMobileOpen(true)} aria-label="Abrir menú">
            <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 6h16M4 12h16M4 18h16" /></svg>
          </button>
          <Breadcrumb nav={nav} cliente={cliente} />
          <div className="flex items-center gap-3">
            <button type="button" onClick={() => setPaleta(true)} className="cursor-pointer rounded-md p-1.5 text-slate-500 hover:bg-slate-100 lg:hidden" aria-label="Buscar">⌕</button>
            <ContenedorWidget />
            <FxWidget />
            <span className="hidden text-xs text-slate-500 sm:inline">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={() => signOut()}>Salir</Button>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          <ErrorPagina key={pathname}>
            <Suspense fallback={<div className="p-4 text-sm text-slate-500">Cargando…</div>}>{children ?? <Outlet />}</Suspense>
          </ErrorPagina>
        </main>
      </div>

      <CommandPalette open={paleta} onClose={() => setPaleta(false)} clienteCodigo={cliente} menu={menuPlano} />
      <Modal open={atajos} onClose={() => setAtajos(false)} title="Atajos de teclado" size="sm">
        <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
          {[['Ctrl K', 'Buscar producto, pedido, cotización, proveedor o pantalla'], ['G P', 'Ir a Productos'], ['G O', 'Ir a Pedidos'], ['G C', 'Ir a Cotizaciones'], ['G A', 'Ir al Análisis del cliente'], ['G I', 'Ir al Centro de mando'], ['Esc', 'Cerrar el buscador o un modal'], ['?', 'Esta ayuda']].map(([k, d]) => (
            <div key={k} className="contents"><dt><kbd className="rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 font-mono text-xs">{k}</kbd></dt><dd className="text-slate-700">{d}</dd></div>
          ))}
        </dl>
      </Modal>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Breadcrumb: jerarquía derivada del NAV del cliente activo; en un detalle
// muestra el nombre del registro (useTituloRegistro) en vez de «Detalle».
// ─────────────────────────────────────────────────────────────────────

function Breadcrumb({ nav, cliente }: { nav: NavSection[]; cliente: ClienteCodigo }) {
  const location = useLocation();
  const titulo = useTituloActual();
  const crumbs = buildCrumbs(nav, location.pathname, titulo);
  if (crumbs.length === 0) return <div className="flex-1" />;
  return (
    <nav aria-label="Migas de pan" className="flex flex-1 items-center gap-1 overflow-hidden px-3 text-xs text-slate-500">
      {location.pathname.match(/^\/(game|ticnova)(\/|$)/) && <span className="mr-1 rounded bg-slate-100 px-1.5 py-0.5 font-bold text-slate-600">{cliente}</span>}
      {crumbs.map((c, idx) => {
        const isLast = idx === crumbs.length - 1;
        return (
          <span key={`${c.label}-${idx}`} className="flex min-w-0 items-center gap-1">
            {idx > 0 && <span className="mx-1 shrink-0 select-none text-slate-300">›</span>}
            {c.to && !isLast
              ? <Link to={c.to} className="cursor-pointer whitespace-nowrap hover:text-slate-700 hover:underline">{c.label}</Link>
              : <span className={`truncate ${isLast ? 'font-medium text-slate-700' : ''}`} title={c.label}>{c.label}</span>}
          </span>
        );
      })}
    </nav>
  );
}

function buildCrumbs(nav: NavSection[], path: string, titulo: string | null): { label: string; to?: string }[] {
  if (path === '/') return [];
  const detalle = titulo ?? 'Detalle';
  type Crumb = { label: string; to?: string };
  // Gana la entrada MÁS ESPECÍFICA, no la primera del menú: con «Panel GAME»
  // (/game) por delante de «Análisis» (/game/analisis), quedarse con la
  // primera coincidencia dejaba «Panel GAME › Detalle» en todas las pantallas
  // del cliente.
  const candidatos: { largo: number; crumbs: Crumb[] }[] = [];
  const añadir = (to: string, crumbs: Crumb[]) => candidatos.push({ largo: to.length, crumbs });
  for (const section of nav) {
    if (section.kind === 'flat') {
      const to = section.item.to;
      if (to === '/') continue;
      if (path === to) añadir(to, [{ label: section.item.label }]);
      else if (path.startsWith(`${to}/`)) añadir(to, [{ label: section.item.label, to }, { label: detalle }]);
      continue;
    }
    if (section.rootTo && path === section.rootTo) añadir(section.rootTo, [{ label: section.label }]);
    for (const i of section.items) {
      const cabecera: Crumb[] = [{ label: section.label, to: section.rootTo }, { label: i.label, to: i.to }];
      if (path === i.to) añadir(i.to, cabecera);
      else if (path.startsWith(`${i.to}/`)) añadir(i.to, [...cabecera, { label: detalle }]);
    }
  }
  candidatos.sort((a, b) => b.largo - a.largo);
  return candidatos[0]?.crumbs ?? [];
}
